import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, GripVertical, Check, X } from "lucide-react";
import {
  deploymentRequestFieldsApi,
  DeploymentRequestField,
} from "../../api/client";

const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "url", label: "URL" },
  { value: "email", label: "Email" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "textarea", label: "Text Area" },
  { value: "select", label: "Select (Dropdown)" },
];

export default function RequestFieldsSettings() {
  const [showForm, setShowForm] = useState(false);
  const [editingField, setEditingField] =
    useState<DeploymentRequestField | null>(null);
  const [options, setOptions] = useState<string[]>([]);
  const [newOption, setNewOption] = useState("");

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["deployment-request-fields"],
    queryFn: () => deploymentRequestFieldsApi.list(),
  });

  const createMutation = useMutation({
    mutationFn: deploymentRequestFieldsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["deployment-request-fields"],
      });
      handleCancel();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      ...data
    }: { id: string } & Parameters<
      typeof deploymentRequestFieldsApi.update
    >[1]) => deploymentRequestFieldsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["deployment-request-fields"],
      });
      handleCancel();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deploymentRequestFieldsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["deployment-request-fields"],
      });
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const fieldType = formData.get("field_type") as string;

    const fieldData = {
      name: formData.get("name") as string,
      slug: (formData.get("slug") as string) || undefined,
      field_type: fieldType,
      description: formData.get("description") as string,
      placeholder: formData.get("placeholder") as string,
      default_value: formData.get("default_value") as string,
      is_required: formData.get("is_required") === "on",
      options: fieldType === "select" ? options : undefined,
      display_order: editingField?.display_order ?? data?.fields?.length ?? 0,
      is_active: editingField?.is_active ?? true,
    };

    if (editingField) {
      updateMutation.mutate({ id: editingField.id, ...fieldData });
    } else {
      createMutation.mutate(fieldData);
    }
  };

  const handleEdit = (field: DeploymentRequestField) => {
    setEditingField(field);
    setOptions(field.options || []);
    setShowForm(true);
  };

  const handleDelete = (id: string, name: string) => {
    if (confirm(`Are you sure you want to delete the "${name}" field?`)) {
      deleteMutation.mutate(id);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingField(null);
    setOptions([]);
    setNewOption("");
  };

  const handleAddOption = () => {
    if (newOption.trim() && !options.includes(newOption.trim())) {
      setOptions([...options, newOption.trim()]);
      setNewOption("");
    }
  };

  const handleRemoveOption = (option: string) => {
    setOptions(options.filter((o) => o !== option));
  };

  const toggleActive = async (field: DeploymentRequestField) => {
    await updateMutation.mutateAsync({
      id: field.id,
      name: field.name,
      field_type: field.field_type,
      description: field.description || "",
      placeholder: field.placeholder || "",
      default_value: field.default_value || "",
      is_required: field.is_required,
      options: field.options,
      display_order: field.display_order,
      is_active: !field.is_active,
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Request Fields
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Configure custom fields for deployment approval requests (e.g., Jira
            ticket, release notes)
          </p>
        </div>
        <button
          onClick={() => {
            setShowForm(true);
            setEditingField(null);
            setOptions([]);
          }}
          className="btn btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Field
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="mt-6 card">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            {editingField ? "Edit Field" : "Add Field"}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="label">
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  className="input"
                  placeholder="Jira Ticket"
                  defaultValue={editingField?.name}
                  required
                />
              </div>
              <div>
                <label htmlFor="slug" className="label">
                  Slug (auto-generated if empty)
                </label>
                <input
                  type="text"
                  name="slug"
                  id="slug"
                  className="input"
                  placeholder="jira-ticket"
                  defaultValue={editingField?.slug}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="field_type" className="label">
                  Field Type
                </label>
                <select
                  name="field_type"
                  id="field_type"
                  className="input"
                  defaultValue={editingField?.field_type || "text"}
                  required
                >
                  {FIELD_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="default_value" className="label">
                  Default Value
                </label>
                <input
                  type="text"
                  name="default_value"
                  id="default_value"
                  className="input"
                  placeholder="Optional default value"
                  defaultValue={editingField?.default_value}
                />
              </div>
            </div>

            <div>
              <label htmlFor="description" className="label">
                Description (help text)
              </label>
              <input
                type="text"
                name="description"
                id="description"
                className="input"
                placeholder="Link to the Jira ticket for this deployment"
                defaultValue={editingField?.description}
              />
            </div>

            <div>
              <label htmlFor="placeholder" className="label">
                Placeholder
              </label>
              <input
                type="text"
                name="placeholder"
                id="placeholder"
                className="input"
                placeholder="PROJ-123"
                defaultValue={editingField?.placeholder}
              />
            </div>

            {/* Options for select type */}
            <div id="options-section">
              <label className="label">Options (for select type)</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newOption}
                  onChange={(e) => setNewOption(e.target.value)}
                  onKeyPress={(e) =>
                    e.key === "Enter" && (e.preventDefault(), handleAddOption())
                  }
                  className="input flex-1"
                  placeholder="Add option..."
                />
                <button
                  type="button"
                  onClick={handleAddOption}
                  className="btn btn-secondary"
                >
                  Add
                </button>
              </div>
              {options.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {options.map((option, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700"
                    >
                      {option}
                      <button
                        type="button"
                        onClick={() => handleRemoveOption(option)}
                        className="ml-2 text-gray-500 hover:text-gray-700"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="is_required"
                id="is_required"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                defaultChecked={editingField?.is_required}
              />
              <label
                htmlFor="is_required"
                className="ml-2 text-sm text-gray-700"
              >
                Required field
              </label>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCancel}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {editingField ? "Update Field" : "Create Field"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Fields List */}
      <div className="mt-6">
        {isLoading ? (
          <div className="card">
            <p className="text-gray-500">Loading fields...</p>
          </div>
        ) : !data?.fields?.length ? (
          <div className="card">
            <p className="text-gray-500 text-center py-8">
              No custom fields configured. Add fields to capture additional
              information during approval requests.
            </p>
          </div>
        ) : (
          <div className="card overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Field
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Required
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.fields.map((field) => (
                  <tr
                    key={field.id}
                    className={!field.is_active ? "bg-gray-50" : ""}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <GripVertical className="h-4 w-4 text-gray-400 mr-2 cursor-grab" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {field.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {field.slug}
                          </div>
                          {field.description && (
                            <div className="text-xs text-gray-400">
                              {field.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-700">
                        {FIELD_TYPES.find((t) => t.value === field.field_type)
                          ?.label || field.field_type}
                      </span>
                      {field.field_type === "select" && field.options && (
                        <div className="text-xs text-gray-500 mt-1">
                          {field.options.length} options
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {field.is_required ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <X className="h-4 w-4 text-gray-400" />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => toggleActive(field)}
                        className={`px-2 py-1 text-xs font-medium rounded ${
                          field.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {field.is_active ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(field)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(field.id, field.name)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
